/**
 * TRAD-2239 — 중앙정렬 툴팁이 뷰포트 좌우 가장자리에서 화면 밖으로 나가는 것을 막는 순수 계산.
 *
 * 배경: `left-1/2 -translate-x-1/2` 로 앵커 중심에 맞춘 툴팁은, 앵커가 화면 가장자리에 있으면
 * 툴팁 박스의 절반이 뷰포트 밖으로 나간다(RankerBadge 실측: 320px 뷰포트 296개 좌표 중 106개
 * 이탈, 최대 49.5px). 박스 폭이 `max-w-[130px]`로 고정이라 **문구 길이와 무관하게** 발생한다.
 *
 * 여기서는 DOM 을 만지지 않고 "얼마나 밀어야 하는가(px)"만 계산한다 — 측정(사이드 이펙트)은
 * 호출부가 하고, 이 함수는 같은 입력에 같은 출력을 내는 순수 함수라 단위 테스트로 고정된다.
 */

/** 뷰포트 가장자리와 툴팁 사이에 남길 최소 여백(px). */
export const TOOLTIP_VIEWPORT_MARGIN = 8;

interface ClampTooltipOffsetArgs {
  /** 앵커(툴팁의 containing block) 중심의 뷰포트 X 좌표. */
  anchorCenter: number;
  /** 툴팁 박스의 실제 렌더 폭. */
  tooltipWidth: number;
  /** 뷰포트 폭(스크롤바 제외 권장 — `document.documentElement.clientWidth`). */
  viewportWidth: number;
  /** 좌우 최소 여백. 기본 {@link TOOLTIP_VIEWPORT_MARGIN}. */
  margin?: number;
}

/**
 * 중앙정렬을 기본으로 두되, 뷰포트를 벗어나는 만큼만 안쪽으로 밀어주는 보정값(px)을 돌려준다.
 *
 * - 가운데에 있어 이탈이 없으면 **0** — 즉 기존 중앙정렬이 그대로 유지된다(모두 왼쪽으로
 *   붙여버리는 구현과 구분되는 지점).
 * - 왼쪽으로 벗어나면 양수(오른쪽으로 밀기), 오른쪽으로 벗어나면 음수(왼쪽으로 밀기).
 * - 툴팁이 뷰포트보다 넓어 양쪽을 다 만족할 수 없으면 왼쪽 경계에 맞춘다(글의 시작을 살린다).
 * - 측정값이 아직 없거나(0) 비정상(NaN)이면 0 — 보정하지 않고 기존 동작으로 안전하게 떨어진다.
 */
export const clampTooltipOffset = ({
  anchorCenter,
  tooltipWidth,
  viewportWidth,
  margin = TOOLTIP_VIEWPORT_MARGIN,
}: ClampTooltipOffsetArgs): number => {
  const measurable =
    Number.isFinite(anchorCenter) &&
    Number.isFinite(tooltipWidth) &&
    Number.isFinite(viewportWidth) &&
    tooltipWidth > 0 &&
    viewportWidth > 0;
  if (!measurable) return 0;

  // 툴팁이 뷰포트보다 넓으면 요청한 여백을 다 줄 수 없다 — 줄 수 있는 만큼만 준다(음수 방지).
  const usableMargin = Math.max(0, Math.min(margin, (viewportWidth - tooltipWidth) / 2));
  const half = tooltipWidth / 2;
  const left = anchorCenter - half;
  const right = anchorCenter + half;

  if (left < usableMargin) return usableMargin - left;
  if (right > viewportWidth - usableMargin) return viewportWidth - usableMargin - right;
  return 0;
};
